import React, { useState, useEffect } from "react";
import axios from "axios";
import { Nav, SpineRail, Footer } from "../components/site/Chrome";
import { Reveal, Eyebrow } from "../components/site/primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Layers, Clock, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TLIF_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  "name": "Transforaminal Lumbar Interbody Fusion (TLIF)",
  "alternateName": "TLIF",
  "description": "TLIF is a spine surgery used to treat problems in the lower back that cause pain, instability, or pressure on the nerves, by removing a damaged disc and fusing two adjacent vertebrae using a bone graft or spacer, screws and rods.",
  "bodyLocation": "Lumbar spine",
  "howPerformed": "Performed under general anaesthesia through a small incision in the lower back, removing the damaged disc through the natural opening at the side of the spine, placing a cage or bone graft in the disc space, and securing the vertebrae with screws and rods to allow fusion.",
  "indication": [
    { "@type": "MedicalIndication", "name": "Spondylolisthesis" },
    { "@type": "MedicalIndication", "name": "Degenerative disc disease" },
    { "@type": "MedicalIndication", "name": "Spinal instability" }
  ],
  "performedBy": {
    "@type": "Physician",
    "name": "Dr. Amit Kumar Chanduka",
    "medicalSpecialty": "https://schema.org/Neurosurgery"
  },
  "areaServed": { "@type": "City", "name": "Bhubaneswar" }
};

const REASONS = [
  "Spondylolisthesis — when one vertebra has slipped forward over the one below it",
  "Facetal arthropathy — when the joints between two adjacent vertebrae become sore and cause nagging pain",
  "Degenerative disc disease with ongoing instability or nerve pressure",
  "A disc that has herniated again after previous surgery (failed back syndrome)",
  "Spinal instability causing significant back or leg pain",
];

const STEPS = [
  "A small incision is made in the lower back.",
  "The surgeon carefully moves the muscles aside (rather than cutting them) to reach the spine.",
  "A cut is made on the bone (laminectomy and facetectomy, unilateral or bilateral depending on the pathology).",
  "The damaged disc is removed through the natural opening at the side of the spine.",
  "A small cage or bone graft is placed in the empty disc space to restore height and support.",
  "Screws and rods are placed to hold the vertebrae steady while the bone fuses.",
  "The incision is closed with a temporary drainage system in place.",
];

const BEFORE = [
  "A pre-anaesthetic checkup is done by the anaesthesiologist.",
  "Follow any fasting instructions given by the hospital (usually no food or water after midnight the night before, or 8 hours prior to surgery).",
  "Tell the team about all medicines and supplements you take, especially blood thinners — some may need to be paused before surgery.",
  "Arrange for someone to help you at home for the first two to three weeks after discharge.",
  "If you smoke, stopping well before surgery helps the fusion heal better.",
];

const AT_HOME = [
  "Gradual increase in walking is encouraged; avoid bending, twisting, or lifting anything heavy for the first six to eight weeks.",
  "Some pain and stiffness at the incision site is normal and is managed with medication.",
  "Physiotherapy is usually introduced 6 to 8 weeks after surgery to rebuild strength safely.",
  "Most people return to light daily activities within four to six weeks, and to work within six to twelve weeks, depending on the nature of the job — desk-based work is usually quicker than physical labour.",
  "Full fusion of the bone continues over several months, so it is important to follow activity guidance even after you start feeling better.",
];

const REDFLAGS = [
  "Fever, or redness/discharge from the incision",
  "New or worsening numbness, weakness, or pain in the legs",
  "Difficulty controlling your bladder or bowel",
  "Severe pain not relieved by your prescribed medication",
];



const SectionTitle = ({ children }) => (
  <h2 className="font-display font-light text-3xl md:text-4xl text-[#161C22]">{children}</h2>
);

const BulletList = ({ items, className = "" }) => (
  <ul className={`space-y-3 ${className}`}>
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-[#161C22]/75 leading-relaxed">
        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#5ECCA0] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const NumberedList = ({ items }) => (
  <ol className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4 text-[#161C22]/75 leading-relaxed">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#E3F0EA] text-[#1F3864] text-sm font-medium flex items-center justify-center font-mono-ui">
          {i + 1}
        </span>
        <span className="pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);

const Callout = ({ title, children, tone = "green" }) => (
  <div className={`rounded-xl border p-6 ${tone === "green" ? "bg-[#E3F0EA] border-[#5ECCA0]/40" : "bg-red-50 border-red-200"}`}>
    <div className={`text-sm font-medium mb-1.5 ${tone === "green" ? "text-[#1F3864]" : "text-red-700"}`}>{title}</div>
    <p className={`text-sm leading-relaxed ${tone === "green" ? "text-[#161C22]/70" : "text-red-700/80"}`}>{children}</p>
  </div>
);

export default function TLIF() {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "TLIF Surgery in Bhubaneswar — Dr. Amit Chanduka";
    return () => { document.title = prevTitle; };
  }, []);

  useEffect(() => {
    axios.get(`${API}/specialization-faqs`, { params: { slug: "tlif" } }).then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#F3F5F4] min-h-screen" data-testid="tlif-page">
      <script type="application/ld+json">{JSON.stringify(TLIF_SCHEMA)}</script>
      <SpineRail />
      <Nav />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#1F3864] text-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <a href="/#specializations" className="inline-flex items-center gap-2 text-white/60 hover:text-[#5ECCA0] text-sm mb-8 transition-colors">
                <ArrowLeft size={16} /> Back to Specializations
              </a>
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-[#5ECCA0] mb-6">
                <Layers size={26} strokeWidth={1.5} />
              </div>
              <Eyebrow tone="onDark">Specialization</Eyebrow>
              <h1 className="font-display font-light text-5xl md:text-6xl mt-5 leading-tight">TLIF</h1>
              <p className="text-white/70 mt-3 text-lg md:text-xl">Transforaminal Lumbar Interbody Fusion — a guide for patients and families</p>
              <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
                A guide by Dr. Amit Chanduka, Neurosurgeon &amp; Spine Surgeon, explaining what TLIF is, why it might be
                recommended, and what to expect before, during, and after surgery.
              </p>
              <a href="/#contact" data-testid="tlif-hero-book-btn"
                className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>

        {/* What is TLIF */}
        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What is TLIF?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                TLIF stands for Transforaminal Lumbar Interbody Fusion. It is one of the most common types of spine
                surgery used to treat problems in the lower back (lumbar spine) that cause pain, instability, or
                pressure on the nerves.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {[
                  ["T", "Transforaminal", "The approach the surgeon takes to reach the target for nerve decompression and fusion."],
                  ["L", "Lumbar", "The region in the lower back of the spine that usually requires this procedure."],
                  ["I", "Interbody", "The place of actual work — the space between two spinal bones, or vertebrae."],
                  ["F", "Fusion", "The ultimate aim: uniting two adjacent spinal bones so that instability is completely cured."],
                ].map(([letter, word, desc]) => (
                  <div key={letter} className="bg-white rounded-xl border border-[#DCE3E0] p-5">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-2xl text-[#5ECCA0]">{letter}</span>
                      <span className="font-medium text-[#161C22]">{word}</span>
                    </div>
                    <p className="text-sm text-[#161C22]/65 mt-2 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-[#161C22]/75 mt-8 leading-relaxed">
                In simple terms: the procedure creates an approach in the lower back to remove the disc material,
                decompress the nerves, and replace the disc space with a bone graft or an artificial spacer, usually
                made of PEEK or titanium. Screws are then placed in the bones for fixation. The entire procedure is
                carried out with the aim of achieving fusion — a permanent union — between two adjacent bones.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Why might I need this */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Why might I need this surgery?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Dr. Chanduka recommends TLIF only when more conservative treatments — such as physiotherapy,
                medication, and injections — have not given enough relief, and when scans clearly show a problem
                that surgery can address. Common reasons TLIF is recommended include:
              </p>
              <BulletList items={REASONS} className="mt-6" />
              <div className="mt-8">
                <Callout title="A note on decisions">
                  Surgery is never the first option and is only suggested after a full discussion of your scans,
                  symptoms, and other treatments. You are always welcome to ask about alternatives before deciding.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        {/* What happens during */}
        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What happens during the procedure</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                TLIF is done under general anaesthesia, so you are fully asleep and feel nothing during the
                operation. Broadly, the steps are:
              </p>
              <div className="mt-6">
                <NumberedList items={STEPS} />
              </div>
              <div className="flex items-center gap-2.5 mt-8 text-[#161C22]/60 text-sm">
                <Clock size={16} className="text-[#5ECCA0]" />
                The operation typically takes two to three hours, depending on how many levels of the spine are
                involved. Your surgical team will give you a time estimate specific to your case.
              </div>
            </Reveal>
          </div>
        </section>

        {/* Preparing for surgery */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Preparing for your surgery</SectionTitle>
              <div className="grid md:grid-cols-2 gap-10 mt-8">
                <div>
                  <h3 className="font-display text-xl text-[#161C22] mb-4">In the days before</h3>
                  <BulletList items={BEFORE} />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#161C22] mb-4">On the day</h3>
                  <p className="text-[#161C22]/75 leading-relaxed">
                    Take any medicines instructed by the anaesthetist in your pre-op advice.
                  </p>
                  <p className="text-[#161C22]/75 leading-relaxed mt-4">
                    The nursing team will check for and arrange blood products if needed — this is usually rare
                    for a single-level surgery in a healthy individual with a good pre-operative haemoglobin
                    level (typically above 12 g%).
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Recovery */}
        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Recovery: what to expect</SectionTitle>
              <div className="grid md:grid-cols-2 gap-10 mt-8">
                <div>
                  <h3 className="font-display text-xl text-[#161C22] mb-4">In hospital</h3>
                  <p className="text-[#161C22]/75 leading-relaxed">
                    Most patients stay in hospital for three to four days after TLIF. You will usually be
                    encouraged to sit up and take a few short, supported walks within a day of surgery — gentle
                    movement early on actually helps recovery. A lumbosacral brace may be recommended.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#161C22] mb-4">At home</h3>
                  <BulletList items={AT_HOME} />
                </div>
              </div>
              <div className="mt-8">
                <Callout title="Every recovery is different">
                  These timelines are general guidance, not a promise. Your own recovery will be discussed with
                  you at each follow-up, based on how your fusion is progressing on scans.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Risks */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Risks and when to call us</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                TLIF is a well-established and generally safe procedure, but like any surgery it carries some
                risk. These are discussed in detail before you consent to surgery, and include infection,
                bleeding, nerve irritation, and the small possibility that the bone does not fuse as expected,
                which can sometimes need further treatment.
              </p>
              <p className="text-[#161C22] font-medium mt-8 mb-4">Please contact the clinic promptly if, after surgery, you notice:</p>
              <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                <ul className="space-y-3">
                  {REDFLAGS.map((item, i) => (
                    <li key={i} className="flex gap-3 text-red-700/90 leading-relaxed text-sm">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Frequently asked questions</SectionTitle>
              {faqs.length > 0 && (
                <Accordion type="single" collapsible className="mt-8">
                  {faqs.map((item) => (
                    <AccordionItem key={item.id} value={item.id} className="border-[#DCE3E0]">
                      <AccordionTrigger className="text-left font-display text-lg text-[#161C22] hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#161C22]/70 leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </Reveal>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 md:py-20 bg-[#1F3864] text-white text-center">
          <div className="max-w-[700px] mx-auto px-5 md:px-8">
            <Reveal>
              <CheckCircle2 size={28} className="text-[#5ECCA0] mx-auto mb-5" />
              <h2 className="font-display font-light text-3xl md:text-4xl">Have questions about TLIF?</h2>
              <p className="text-white/70 mt-4 leading-relaxed">
                This page is for general education and does not replace a personal consultation. Please discuss
                how this applies to your own case with Dr. Chanduka before making any treatment decision.
              </p>
              <a href="/#contact" data-testid="tlif-bottom-book-btn"
                className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
